import { useMemo, useRef, useState } from 'react';
import IconButton from '@material-ui/core/IconButton';
import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import Badge from '@material-ui/core/Badge';
import Fade from '@material-ui/core/Fade';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { ClassNameMap } from '@material-ui/styles/withStyles';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import clsx from 'clsx';

import { useForm, useDragAndDrop, useFileError, useGi18n } from '../hooks';
import { getFileSource } from '../functions';
import { FormImageStylesProps } from '../types';
import { PictureSvg } from '../images';
import { get as _get } from 'lodash';
import FormHelperText from './FormHelperText';
import { FormikValues, setIn } from 'formik';

const imageAccept = ['image/jpg', 'image/png', 'image/jpeg'];

interface FormImageProps {
	id: string;
	noDelete?: boolean;
	classes?: Partial<ClassNameMap<'root' | 'badge' | 'fade' | 'avatar' | 'blank' | 'helperText' | 'error'>>;
	size?: number;
}

const FormImage: React.FC<FormImageProps> = ({ id, noDelete = false, size = 20000, classes: cl = {} }) => {
	const [isHovering, setIsHovering] = useState(false);
	const [fileKey, setFileKey] = useState(`${Math.random()}`);

	const fileInputRef = useRef<HTMLInputElement>(null);

	const form = useForm();
	const fi = useGi18n()(i18n => i18n.error?.form?.formImage);
	const fileError = useFileError(id);
	const classes = useStyles({ error: !!fileError });
	const imagePath = _get(form.values, id);
	let formPath = `${id}File`;



	const helperText = useMemo(() => {
		if (!imageAccept.length) return undefined;

		const fileTypes = imageAccept.map(accept => accept.split('/')[1]);

		return `${fi.type} ${fileTypes.join(', ')}`;
	}, [fi]);

	const updateImage = async (files: FileList | File[] | null) => {
		files = files ?? [];
		const image = files[0];
		if (!image) return form.setFieldTouched(id, true);
		if (!imageAccept.includes(image.type)) return (
			!_get(form.touched, id) && await form.setFieldTouched(id, true),
			form.setFieldError(id, 'error.image')
		);

		if (size && image.size > size) return (
			!_get(form.touched, id) && await form.setFieldTouched(id, true),
			form.setFieldError(id, 'error.tooHeavy')
		);

		const imagePath = await getFileSource(image);
		form.setValues((values: FormikValues) => {
			let temp = setIn(values, formPath, image)
			return setIn(temp, id, imagePath)
		});
		form.setFieldTouched(id, true);
	}

	const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		updateImage(event.target.files);
	}

	const { isDragging, ...dragDropProps } = useDragAndDrop(updateImage);

	const handleHovering = (boolean: boolean) => () => setIsHovering(boolean);

	const handleInputOpen = () => {
		fileInputRef.current?.click();
	}

	const handleDelete = () => {
		form.setFieldTouched(id, true);
		form.setValues((values: FormikValues) => {
			let temp = setIn(values, id, null)
			return setIn(temp, formPath, null)
		});
		setFileKey(`${Math.random()}`);
	}

	return (
		<div className={clsx(classes.root, isDragging && classes.dragging, cl.root)} {...dragDropProps}>
			<div className={classes.rootInner}>
				{imagePath ? (
					<Badge
						overlap="rectangle"
						anchorOrigin={{
							vertical: 'top',
							horizontal: 'right',
						}}
						classes={{
							root: cl.badge,
							badge: classes.badge
						}}
						badgeContent={(
							<Fade in={isHovering}>
								<div
									onMouseEnter={handleHovering(true)}
									onMouseLeave={handleHovering(false)}
									className={cl.badge}
								>
									<IconButton color="primary" onClick={handleInputOpen}>
										<EditIcon />
									</IconButton>
									{!noDelete && (
										<IconButton color="primary" onClick={handleDelete}>
											<DeleteIcon />
										</IconButton>
									)}
								</div>
							</Fade>
						)}
					>
						<Avatar
							alt=""
							src={imagePath}
							variant="square"
							className={clsx(classes.container, classes.avatar, cl.avatar)}
							onMouseEnter={handleHovering(true)}
							onMouseLeave={handleHovering(false)}
						/>
					</Badge>
				) : (
					<div className={clsx(classes.container, classes.blank, cl.blank)}>
						<PictureSvg className={classes.blankSvg} />
						<span className={clsx(classes.blankSpan, classes.text)}>{fi.drag}</span>
						<Button
							color="primary"
							variant="outlined"
							onClick={handleInputOpen}
							classes={{
								root: classes.blankButton,
								label: clsx(classes.text, classes.blankButtonLabel)
							}}
						>
							{fi.addImage}
						</Button>
					</div>
				)}
				<input
					key={fileKey}
					ref={fileInputRef}
					type="file"
					className={classes.input}
					onChange={handleInputChange}
					accept={imageAccept.join('|')}
				/>
				<FormHelperText className={cl.helperText} text={helperText} />
				<FormHelperText className={cl.error} text={fileError} error />
			</div>
		</div>
	);
}

export default FormImage;
const useStyles = makeStyles(theme => ({
	root: {
		transition: '0.2s',
		display: 'flex',
		justifyContent: 'center'
	},

	rootInner: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start'
	},

	dragging: {
		opacity: 0.8,
		transform: 'scale(0.98)',
		'-webkit-transform': 'scale(0.98)',
		'-moz-transform': 'scale(0.98)',
		'-ms-transform': 'scale(0.98)',
		'-o-transform': 'scale(0.98)',
	},

	container: {
		width: 260,
		height: 260,
		borderRadius: 4,

		[theme.breakpoints.up(1600)]: {
			width: 350,
			height: 350,
			borderRadius: 5,
		}
	},

	avatar: {
		border: '0.4px solid #707070',

		[theme.breakpoints.up(1600)]: {
			border: '0.5px solid #707070',
		}
	},

	badge: {
		transform: 'scale(1) translate(0, 0)',
		height: 'auto'
	},

	blank: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'space-between',
		alignItems: 'center',
		paddingTop: 31,
		paddingBottom: 31,
		'--border-color': ({ error }: FormImageStylesProps) => error ? theme.palette.error.main : '#acacac',
		backgroundColor: '#f2f2f2',
		backgroundImage: `linear-gradient(to right, var(--border-color) 50%, transparent 50%),
				 linear-gradient(to right, var(--border-color) 50%, transparent 50%),
				 linear-gradient(to bottom, var(--border-color) 50%, transparent 50%),
				 linear-gradient(to bottom, var(--border-color) 50%, transparent 50%)`,
		backgroundPosition: 'left top, left bottom, left top, right top',
		backgroundRepeat: 'repeat-x, repeat-x, repeat-y, repeat-y',
		backgroundSize: '11px 1px, 11px 1px, 1px 11px, 1px 11px',

		[theme.breakpoints.up(1600)]: {
			paddingTop: 42,
			paddingBottom: 42,
		}
	},

	blankSvg: {
		width: '40%',
	},

	blankSpan: {
		color: '#BDBDBD',
	},

	text: {
		fontSize: 12,
		fontFamily: 'Assistant-SemiBold',

		[theme.breakpoints.up(1600)]: {
			fontSize: 17
		}
	},

	blankButton: {
		borderRadius: 6.3,
		padding: '3px 13px',

		[theme.breakpoints.up(1600)]: {
			borderRadius: 8.5,
			padding: '4.25px 17.85px'
		}
	},

	blankButtonLabel: {
		textTransform: 'none'
	},

	input: {
		display: 'none'
	}
}));